/**
 * Inactivity Timeout Hook
 * Monitors user activity and triggers sign-out after 30 minutes of inactivity
 * 
 * FINLIST AUTH RULES:
 * - Auto sign-out after 30 minutes of inactivity
 * - Reset timer on: mouse movement, keyboard input, touch events, navigation
 * - Redirect to /welcome after auto sign-out (handled by component using this hook)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

// Inactivity timeout: 30 minutes in milliseconds
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

// Warn user 1 minute before timeout
const WARNING_BEFORE_TIMEOUT_MS = 60 * 1000;

/**
 * Hook to handle user inactivity timeout
 * Only active when user is authenticated
 */
export function useInactivityTimeout() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  
  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  /**
   * Handle auto sign-out due to inactivity
   */
  const handleInactivityLogout = useCallback(async () => {
    clearTimers();
    
    try {
      // Clear any stored route memory
      sessionStorage.removeItem('finlist_last_route');
      localStorage.removeItem('finlist_last_route');
      
      await logout();
      
      // Show message to user
      toast('You\'ve been signed out due to inactivity.', {
        icon: '⏱️',
        duration: 5000,
      });
      
      // Navigate to welcome page
      navigate('/welcome', { replace: true });
    } catch (error) {
      console.error('[InactivityTimeout] Logout error:', error);
      // Force navigation even if logout fails
      navigate('/welcome', { replace: true });
    }
  }, [logout, navigate, clearTimers]);

  /**
   * Show warning before timeout
   */
  const showWarning = useCallback(() => {
    toast('You will be signed out in 1 minute due to inactivity.', {
      icon: '⚠️',
      duration: 10000,
    });
  }, []);

  /**
   * Reset the inactivity timer
   */
  const resetTimer = useCallback(() => {
    if (!currentUser) return;
    
    lastActivityRef.current = Date.now();
    clearTimers();
    
    // Set warning timer (1 minute before logout)
    const warningTime = INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS;
    warningRef.current = setTimeout(showWarning, warningTime);
    
    // Set logout timer
    timeoutRef.current = setTimeout(handleInactivityLogout, INACTIVITY_TIMEOUT_MS);
  }, [currentUser, clearTimers, showWarning, handleInactivityLogout]);

  /**
   * Activity event handler
   */
  const handleActivity = useCallback(() => {
    // Throttle: only reset if more than 1 second since last activity
    if (Date.now() - lastActivityRef.current > 1000) {
      resetTimer();
    }
  }, [resetTimer]);

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    // Only monitor activity when user is authenticated
    if (!currentUser) {
      clearTimers();
      return;
    }

    // Activity events to monitor
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'keypress',
      'touchstart',
      'touchmove',
      'scroll',
      'wheel',
      'click',
      'focus'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also reset on visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we've been away too long
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT_MS) {
          handleInactivityLogout();
        } else {
          handleActivity();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimers();
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, handleActivity, handleInactivityLogout, resetTimer, clearTimers]);

  // Return resetTimer for manual resets (e.g., on navigation or API calls)
  return { resetTimer, lastActivity: lastActivityRef.current };
}

export default useInactivityTimeout;
